import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function BugCumulativeFlowChart({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(320);
  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <AreaChart
        data={points}
        margin={{ ...dynamicsBucketChartMargins }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
          if (onPointClick && typeof label === 'string') onPointClick(label);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <Legend />
        <Area
          type="monotone"
          dataKey="open"
          stackId="1"
          stroke="#ef4444"
          fill="#ef4444"
          name="Open"
        />
        <Area
          type="monotone"
          dataKey="inProgress"
          stackId="1"
          stroke="#f97316"
          fill="#f97316"
          name="In progress"
        />
        <Area
          type="monotone"
          dataKey="inQa"
          stackId="1"
          stroke="#0ea5e9"
          fill="#0ea5e9"
          name="In QA"
        />
        <Area
          type="monotone"
          dataKey="done"
          stackId="1"
          stroke="#22c55e"
          fill="#22c55e"
          name="Done"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export const DynamicsB_BugCumulativeFlow: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="B"
      title="Bug cumulative flow"
      hint="Where bugs are sitting at the end of each period: Open / In progress / In QA / Done. Widening bands on Open or In QA mean the backlog is outpacing QA."
      needs="Bug-like issue types set in Quality settings. Use Group by = Sprint for best signal."
      metric="bug-flow"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={320}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.B.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.B.bullets]}
    >
      {(points, onPointClick) => (
        <BugCumulativeFlowChart points={points} onPointClick={onPointClick} />
      )}
    </DynamicsChartFrame>
  );
};
